import { faFilter, faPlus, faBolt, faChartLine, faRocket, faSearchDollar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import SearchBox from "components/common/SearchBox";
import { dealsBreadcrumbItems } from "data/commonData";
import { dealColumnsData } from "data/crm/deals";
import { ChangeEvent, useEffect, useState } from "react";
import { Col, Row, Badge } from "react-bootstrap";
import { useMainLayoutContext } from "providers/MainLayoutProvider";
import { DragDropContext, DropResult } from "react-beautiful-dnd";
import DealColumn from "components/modules/crm/deals/DealColumn";
import AddDealModal from "components/modules/crm/deals/AddDealModal";
import FilterDealsModal from "components/modals/FilterDealsModal";
import PhoenixDroppable from "components/base/PhoenixDroppable";
import DealsAddStageModal from "components/modals/DealsAddStageModal";
import DealsProvider, { useDealsContext } from "providers/CrmDealsProvider";
import Backdrop from "components/common/Backdrop";
import { useAtom } from "jotai";
import axiosInstance from "helpers/api";
import { dealsAtom } from "jotai/LeadsJotaiInstance";
import toast from "react-hot-toast";
import _, { debounce } from "lodash";
import { user_info } from "jotai/UserJotaiInstance";
import CustomLoader from "components/common/CustomLoader";
import { UserInfo } from "interfaces/user";

const DealsPage = () => {
  const [userInfo] = useAtom<UserInfo>(user_info);
  const [showDeals, setShowDeals] = useState(true);
  const isAuthorized = userInfo?.payment_status === "Cancelled" ? false : true;

  useEffect(() => {
    if (!isAuthorized) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "childList") {
            const backdropExists = document.getElementById("kairos-backdrop");
            if (!backdropExists) {
              setShowDeals(false);
            }
          }
        });
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
      return () => observer.disconnect();
    }
  }, [isAuthorized]);

  return (
    <DealsProvider data={dealColumnsData}>
      {(isAuthorized || showDeals) && <Deals />}
      {!isAuthorized && <Backdrop />}
    </DealsProvider>
  );
};

const Deals = () => {
  const [status, setStatus] = useState("idle");
  const { setContentClass } = useMainLayoutContext();
  const [searchInput, setSearchInput] = useState("");
  const [deals, setDeals] = useAtom(dealsAtom);
  const [isLoading, setIsLoading] = useState(false);
  const [stage, setStage] = useState(0);
  const {
    openAddDealModal,
    setOpenAddDealModal,
    openFilterDealModal,
    setOpenFilterDealModal,
    openAddStageModal,
    setOpenAddStageModal,
    // handleDragEnd,
  } = useDealsContext();

  const debouncedFetchDealData = debounce(async (searchValue) => {
    try {
      const response = await axiosInstance.get("/deals", {
        params: {
          search: searchValue,
        },
      });
      setDeals(response.data.data);
    } catch (error: any) {
      console.log(error.response.data.message);
    } finally {
      setStatus("idle")
    }
  }, 500);

  useEffect(() => {
    if (searchInput !== undefined) {
      setStatus("loading");
      debouncedFetchDealData(searchInput);
    }
    return () => {
      setStatus("idle")
      debouncedFetchDealData.cancel();
    };
  }, [searchInput]);

  useEffect(() => {
    setContentClass("vh-100");
    return () => {
      setContentClass("");
    };
  }, []);

  const handleDealModal = (id: any) => {
    setStage(id);
    setOpenAddDealModal(true);
  };

  const handleCloseDealModal = () => {
    setOpenAddDealModal(false);
    setStage(0);
  };

  const handleDragEnd = async (result: DropResult) => {
    const { source, destination, draggableId } = result;

    if (!destination) return;

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const updatedDeals = JSON.parse(JSON.stringify(deals));

    const sourceStageIndex = updatedDeals.findIndex(
      (stage: any) => stage.id === parseInt(source.droppableId)
    );
    const destinationStageIndex = updatedDeals.findIndex(
      (stage: any) => stage.id === parseInt(destination.droppableId)
    );

    if (sourceStageIndex === -1 || destinationStageIndex === -1) {
      toast.error("Source or destination stage not found!");
      return;
    }

    const dealToMove = updatedDeals[sourceStageIndex].deals.find(
      (deal: any) => deal.id === draggableId
    );
    if (!dealToMove) {
      toast.error("Deal not found!");
      return;
    }

    updatedDeals[sourceStageIndex].deals.splice(source.index, 1);

    updatedDeals[destinationStageIndex].deals.splice(
      destination.index,
      0,
      dealToMove
    );

    updatedDeals[destinationStageIndex].deals.forEach(
      (deal: any, index: number) => {
        deal.deal_index = index;
      }
    );

    if (source.droppableId === destination.droppableId) {
      updatedDeals[sourceStageIndex].deals.forEach(
        (deal: any, index: number) => {
          deal.deal_index = index;
        }
      );
    }

    setDeals(updatedDeals);

    try {
      const response = await axiosInstance.patch(
        `/deals/${draggableId}/deal-stages/${destination.droppableId}`,
        { updatedDeals }
      );
      toast.success(response.data.message);
    } catch (error: any) {
      console.error(error.response?.data?.message || "An error occurred");
    }
  };

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handleOpenAddDealModal = () => {
    setOpenAddDealModal(true);
    setStage(0);
  };

  return (
    <div className="d-flex flex-column h-100">
      <PageBreadcrumb items={dealsBreadcrumbItems} />
      
      {/* AI Intelligence Header */}
      <div 
        className="intelligence-header mb-4"
        style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <div style={{ position: 'relative', marginRight: '16px' }}>
              <FontAwesomeIcon 
                icon={faRocket} 
                style={{ 
                  color: '#00d4ff', 
                  fontSize: '32px',
                  filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.5))' 
                }} 
              />
            </div>
            <div>
              <h2 style={{ 
                color: '#1f2937', 
                fontWeight: 600, 
                marginBottom: '4px',
                textShadow: '0 0 10px rgba(0, 212, 255, 0.2)' 
              }}>
                Deal Intelligence Pipeline
              </h2>
              <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                AI-powered deal management and revenue optimization
              </p>
            </div>
          </div>
          
          {/* AI Status Indicator */}
          <div 
            className="d-flex align-items-center"
            style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              padding: '8px 16px',
              borderRadius: '20px',
              border: '1px solid rgba(16, 185, 129, 0.3)'
            }}
          >
            <div 
              className="rounded-circle me-2"
              style={{
                width: '8px',
                height: '8px',
                background: '#10b981',
                animation: 'pulse 2s infinite'
              }}
            />
            <span 
              className="fw-semibold text-success"
              style={{ 
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              AI ACTIVE
            </span>
          </div>
        </div>
      </div>

      {/* Enhanced Controls Section */}
      <div 
        className="mb-6"
        style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '16px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          backdropFilter: 'blur(10px)',
          padding: '24px'
        }}
      >
        <Row className="g-3 justify-content-between align-items-center">
          <Col xs="auto">
            <div className="d-flex align-items-center gap-3">
              <Button
                variant="primary"
                className="ai-enhanced-btn"
                startIcon={<FontAwesomeIcon icon={faPlus} className="me-2" />}
                onClick={handleOpenAddDealModal}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '12px 24px',
                  fontWeight: '600',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
                }}
              >
                Add Deal
              </Button>
              
              {/* AI Metrics Badge */}
              <div 
                className="metric-badge"
                style={{
                  background: 'rgba(16, 185, 129, 0.1)',
                  borderRadius: '12px',
                  padding: '8px 16px',
                  border: '1px solid rgba(16, 185, 129, 0.2)'
                }}
              >
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faChartLine} className="text-success me-2" />
                  <div>
                    <div className="d-flex align-items-center gap-1">
                      <span className="fw-bold text-success" style={{ fontSize: '14px' }}>
                        {deals?.reduce((total: number, stage: any) => total + (stage?.deals?.length || 0), 0)}
                      </span>
                      <small className="text-muted" style={{ fontSize: '10px' }}>Active Deals</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Col>
          
          <Col xs="auto">
            <div className="d-flex align-items-center gap-2">
              <div 
                className="search-container"
                style={{
                  position: 'relative',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  border: '1px solid rgba(0, 212, 255, 0.2)',
                  backdropFilter: 'blur(5px)'
                }}
              >
                <SearchBox
                  onChange={handleSearchInputChange}
                  placeholder="AI-powered search..."
                  className="ai-search-box"
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: '#1f2937'
                  }}
                />
                <FontAwesomeIcon 
                  icon={faSearchDollar} 
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#00d4ff',
                    pointerEvents: 'none'
                  }}
                />
              </div>
              
              <Button
                variant="outline-primary"
                className="ai-filter-btn"
                onClick={() => setOpenFilterDealModal(true)}
                style={{
                  borderRadius: '12px',
                  padding: '10px 16px',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                  background: 'rgba(0, 212, 255, 0.05)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 212, 255, 0.1)';
                  e.currentTarget.style.borderColor = '#00d4ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 212, 255, 0.05)';
                  e.currentTarget.style.borderColor = 'rgba(0, 212, 255, 0.3)';
                }}
              >
                <FontAwesomeIcon
                  icon={faFilter}
                  className="text-primary"
                  style={{ color: '#00d4ff' }}
                />
              </Button>
            </div>
          </Col>
        </Row>
      </div>
      {/* Enhanced Loading State */}
      {status === "loading" && (
        <div
          className="d-flex flex-column justify-content-center align-items-center"
          style={{ 
            height: "60vh",
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
            borderRadius: '16px',
            border: '1px solid rgba(0, 212, 255, 0.2)',
            backdropFilter: 'blur(15px)'
          }}
        >
          <div className="mb-3">
            <FontAwesomeIcon 
              icon={faBolt} 
              style={{ 
                color: '#00d4ff',
                fontSize: '48px',
                filter: 'drop-shadow(0 0 20px rgba(0, 212, 255, 0.5))',
                animation: 'pulse 2s infinite'
              }}
            />
          </div>
          <CustomLoader />
          <p className="text-muted mt-3" style={{ fontSize: '14px' }}>
            AI processing your deals pipeline...
          </p>
        </div>
      )}
      
      {/* Enhanced Deal Columns Container */}
      {status === "idle" && deals.length > 0 && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div 
            className="mx-n4 px-4 mx-lg-n6 px-lg-6 flex-1 d-flex gap-4 scrollbar"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.02) 0%, rgba(255, 255, 255, 0.01) 100%)',
              borderRadius: '16px',
              padding: '20px',
              backdropFilter: 'blur(5px)'
            }}
          >
            {deals?.length > 0 &&
              deals?.map((col: any) => (
                <PhoenixDroppable key={col.id} droppableId={col.id.toString()}>
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      <DealColumn
                        column={col}
                        handleOpenAddModal={handleDealModal}
                        key={col.id}
                      />
                      {provided.placeholder as React.ReactNode}
                    </div>
                  )}
                </PhoenixDroppable>
              ))}
            
            {/* Enhanced Add Stage Section */}
            <div 
              className="deals-column flex-center flex-shrink-0"
              style={{
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                border: '2px dashed rgba(0, 212, 255, 0.3)',
                borderRadius: '16px',
                padding: '24px',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease',
                minHeight: '200px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#00d4ff';
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(0, 212, 255, 0.3)';
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)';
              }}
            >
              <div className="text-center">
                <FontAwesomeIcon 
                  icon={faPlus} 
                  style={{ 
                    color: '#00d4ff',
                    fontSize: '24px',
                    marginBottom: '16px',
                    filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.3))'
                  }}
                />
                <h5 className="mb-4" style={{ color: '#1f2937', fontWeight: 600 }}>
                  Add New Stage
                </h5>
                <Button
                  variant="primary"
                  size="sm"
                  startIcon={<FontAwesomeIcon icon={faPlus} />}
                  onClick={() => setOpenAddStageModal(true)}
                  style={{
                    background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '8px 16px',
                    fontWeight: '600',
                    boxShadow: '0 4px 15px rgba(0, 212, 255, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 212, 255, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 212, 255, 0.3)';
                  }}
                >
                  New Stage
                </Button>
              </div>
            </div>
          </div>
        </DragDropContext>
      )}

      <AddDealModal
        stage={stage}
        show={openAddDealModal}
        handleClose={handleCloseDealModal}
      />
      <FilterDealsModal
        show={openFilterDealModal}
        handleClose={() => setOpenFilterDealModal(false)}
      />
      <DealsAddStageModal
        show={openAddStageModal}
        handleClose={() => setOpenAddStageModal(false)}
      />

      {/* AI Enhanced Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
        /* AI Color Variables */
        :root {
          --ai-primary: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          --ai-accent: #00d4ff;
          --ai-glow: rgba(0, 212, 255, 0.3);
          --ai-success: #10b981;
          --ai-warning: #f59e0b;
          --ai-danger: #ef4444;
          --ai-info: #3b82f6;
          --ai-surface: rgba(255, 255, 255, 0.05);
          --ai-surface-elevated: rgba(255, 255, 255, 0.1);
          --ai-glass-bg: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
          --ai-text-primary: #1f2937;
          --ai-text-secondary: #6b7280;
          --ai-border-primary: rgba(0, 212, 255, 0.2);
        }

        /* AI Pulse Animation */
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        @keyframes ai-glow-pulse {
          0%, 100% { box-shadow: 0 0 20px rgba(0, 212, 255, 0.4); }
          50% { box-shadow: 0 0 30px rgba(0, 212, 255, 0.6); }
        }

        /* Enhanced Search Box */
        .ai-search-box input {
          background: transparent !important;
          border: none !important;
          color: var(--ai-text-primary) !important;
          padding-right: 40px !important;
        }

        .ai-search-box input::placeholder {
          color: var(--ai-text-secondary) !important;
          font-style: italic;
        }

        .ai-search-box input:focus {
          box-shadow: 0 0 0 2px rgba(0, 212, 255, 0.3) !important;
          outline: none !important;
        }

        /* Enhanced Button Styles */
        .ai-enhanced-btn:hover {
          transform: translateY(-2px) !important;
          box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4) !important;
        }

        .ai-filter-btn:hover {
          background: rgba(0, 212, 255, 0.1) !important;
          border-color: var(--ai-accent) !important;
        }

        /* Glassmorphism Enhancement */
        .intelligence-header {
          backdrop-filter: blur(10px) !important;
          -webkit-backdrop-filter: blur(10px) !important;
        }

        /* Scrollbar Enhancement */
        .scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
        }

        .scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, rgba(0, 212, 255, 0.3) 0%, rgba(102, 126, 234, 0.3) 100%);
          border-radius: 10px;
          border: 2px solid transparent;
        }

        .scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, rgba(0, 212, 255, 0.5) 0%, rgba(102, 126, 234, 0.5) 100%);
        }

        /* Enhanced Container Transitions */
        .search-container:hover {
          border-color: var(--ai-accent) !important;
          box-shadow: 0 0 0 2px rgba(0, 212, 255, 0.1) !important;
        }

        /* AI Metric Badge Animation */
        .metric-badge {
          transition: all 0.3s ease;
        }

        .metric-badge:hover {
          transform: scale(1.05);
          box-shadow: 0 4px 15px rgba(16, 185, 129, 0.2);
        }

        /* Enhanced Responsive Design */
        @media (max-width: 768px) {
          .intelligence-header {
            padding: 16px !important;
          }
          
          .intelligence-header h2 {
            font-size: 1.2rem !important;
          }
          
          .metric-badge {
            padding: 6px 12px !important;
          }
          
          .ai-enhanced-btn {
            padding: 8px 16px !important;
          }
        }

        /* Neural Network Aesthetic */
        .intelligence-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 20% 20%, rgba(0, 212, 255, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(102, 126, 234, 0.1) 0%, transparent 50%);
          pointer-events: none;
          border-radius: 16px;
        }
        `
      }} />
    </div>
  );
};

export default DealsPage;
